import React from 'react';
import RGL from 'react-grid-layout';
import { Controller, useForm } from 'react-hook-form';

import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { useDashboard } from 'dashboards/hooks/useDashboard';
import { useDeployments } from 'hooks/api/useDeployments';
import { useGateways } from 'gateways/hooks/useGateways';
import { useModal } from 'hooks/useModal';
import { useStreams, UseStreamsParams } from 'streams/hooks/useStreams';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';

import { MultiStreamWidget } from 'types/dashboard_widget';
import { StreamSource } from 'types/stream';

import { Button } from 'components/Button/Button';
import { DeploymentSelect } from 'deployments/components/DeploymentSelect/DeploymentSelect';
import { Field } from 'components/Field/Field';
import { GatewaySelect } from 'gateways/components/GatewaySelect/GatewaySelect';
import { Icon } from 'components/Icon/Icon';
import { IconButton } from 'components/IconButton/IconButton';
import { Input } from 'components/Input/Input';
import { LightSwitch } from 'components/LightSwitch/LightSwitch';
import { Loader } from 'components/Loader/Loader';
import { Select } from 'components/Select/Select';
import { StreamSelect } from 'streams/components/StreamSelect/StreamSelect';

import { WidgetFormHeader } from './Header';
import { WidgetFormFooter } from './Footer';

const STREAM_SOURCE_OPTIONS = [
  {
    label: 'URI Stream',
    value: 'uri_stream',
  },
  {
    label: 'Camera Stream',
    value: 'camera_stream',
  },
  {
    label: 'Pipeline Stream',
    value: 'pipeline_stream',
  },
];

const FILTER_OPTIONS = [
  { label: 'Deployments', value: 'deployment_ids' },
  { label: 'Gateways', value: 'gateway_ids' },
  { label: 'Stream sources', value: 'sources' },
  { label: 'Streams', value: 'stream_ids' },
] as const;

type MultiStreamWidgetFieldValues = {
  name: string;
  gateway_ids: Array<string>;
  deployment_ids: Array<string>;
  stream_ids: Array<string>;
  sources: Array<StreamSource>;
  showOnlineStreamsOnly: boolean;
  autoPlay: boolean;
};

type MultiStreamFormProps = { widget?: MultiStreamWidget; dashboardId: string };

export function MultiStreamForm({ widget, dashboardId }: MultiStreamFormProps) {
  const { close } = useModal();

  const { data: dashboard } = useDashboard(dashboardId);

  const options = widget ? widget.options : {};

  const {
    deployment_ids = [],
    gateway_ids = [],
    sources = [],
    stream_ids = [],
  } = options;

  const { data: initialGateways, isInitialLoading: isGatewaysLoading } =
    useGateways({
      params: {
        gateway_ids,
        limit: gateway_ids.length,
      },
      enabled: gateway_ids.length > 0,
    });

  const { data: initialDeployments, isInitialLoading: isDeploymentsLoading } =
    useDeployments(
      {
        deployment_ids,
        limit: deployment_ids.length,
      },
      {
        enabled: deployment_ids.length > 0,
      }
    );

  const { data: initialStreams, isInitialLoading: isStreamsLoading } =
    useStreams({
      params: {
        stream_ids,
        limit: stream_ids.length,
      },
      enabled: stream_ids.length > 0,
    });

  const isLoading =
    isGatewaysLoading || isDeploymentsLoading || isStreamsLoading;

  const { mutate, isLoading: isUpdating } = useUpdateDashboard({
    onSuccess: () => close(),
  });

  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    setValue,
    register,
  } = useForm<MultiStreamWidgetFieldValues>({
    defaultValues: {
      name: widget?.name,
      showOnlineStreamsOnly: options?.status?.includes('online'),
      autoPlay: widget?.autoPlay ?? true,
      gateway_ids,
      deployment_ids,
      stream_ids,
      sources,
    },
  });

  const isDirty = Boolean(Object.keys(dirtyFields).length);

  const [filters, setFilters] = React.useState<
    Array<{
      entityType:
        | 'gateway_ids'
        | 'deployment_ids'
        | 'stream_ids'
        | 'sources'
        | 'undecided';
    }>
  >(() => {
    const initialFilters = [];

    if (gateway_ids.length > 0) {
      initialFilters.push({
        entityType: 'gateway_ids' as const,
      });
    }

    if (deployment_ids.length > 0) {
      initialFilters.push({
        entityType: 'deployment_ids' as const,
      });
    }

    if (stream_ids.length > 0) {
      initialFilters.push({
        entityType: 'stream_ids' as const,
      });
    }

    if (sources.length > 0) {
      initialFilters.push({
        entityType: 'sources' as const,
      });
    }

    return initialFilters;
  });

  function onSubmit({
    name,
    gateway_ids,
    deployment_ids,
    stream_ids,
    sources,
    showOnlineStreamsOnly,
    autoPlay,
  }: MultiStreamWidgetFieldValues) {
    if (!dashboard) {
      return;
    }

    const id = widget?.id || crypto.randomUUID();
    const layout: Omit<RGL.Layout, 'i'> =
      widget?.layout ||
      getNewWidgetLayout(dashboard.configuration.widgets, 6, 6);

    const options: UseStreamsParams = {
      gateway_ids,
      deployment_ids,
      stream_ids,
      sources,
      status: showOnlineStreamsOnly ? ['online'] : undefined,
    };

    const updatedDashboard = upsertWidget(
      dashboard,
      new MultiStreamWidget(id, name, options, autoPlay, layout)
    );

    mutate({
      id: updatedDashboard.id,
      configuration: updatedDashboard.configuration,
    });
  }

  function getAvailableOptions() {
    return FILTER_OPTIONS.filter(
      (option) =>
        !new Set(
          filters
            .filter((filter) => filter.entityType)
            .map((filter) => filter.entityType)
        ).has(option.value)
    );
  }

  const canAdd = filters.length < FILTER_OPTIONS.length;

  return (
    <form className="flex h-full flex-col" onSubmit={handleSubmit(onSubmit)}>
      <WidgetFormHeader type="multistream" />
      <Field label="Widget name" error={errors.name}>
        <Input
          {...register('name', {
            required: 'Please choose a name for this widget.',
          })}
          type="text"
          autoComplete="off"
          spellCheck="false"
        />
      </Field>
      <Field label="Show online streams only" id="show-online-streams-only">
        <LightSwitch {...register('showOnlineStreamsOnly')} />
      </Field>
      <Field label="Enable autoplay">
        <LightSwitch {...register('autoPlay')} />
      </Field>

      <Field
        label="Filters"
        error={
          errors.gateway_ids ||
          errors.deployment_ids ||
          errors.stream_ids ||
          errors.sources
        }
      >
        {isLoading ? (
          <Loader size="small" text="Loading filters" />
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {filters.map((filter, index) => (
                <div
                  className="grid grid-cols-[1fr_3fr_auto] items-center gap-2"
                  key={filter.entityType}
                >
                  <div>
                    <Select
                      aria-label="Select type"
                      options={getAvailableOptions()}
                      value={FILTER_OPTIONS.find(
                        ({ value }) => value === filter.entityType
                      )}
                      onChange={(option) => {
                        if (!option) return;

                        setFilters((previousFilters) =>
                          previousFilters.map((filter, i) => {
                            if (i === index) {
                              if (filter.entityType !== 'undecided') {
                                setValue(filter.entityType, []);
                              }
                              return { entityType: option.value };
                            }
                            return filter;
                          })
                        );
                      }}
                    />
                  </div>
                  <div>
                    {filter.entityType !== 'undecided' ? (
                      <Controller
                        control={control}
                        name={filter.entityType}
                        render={({ field: { onChange } }) => {
                          switch (filter.entityType) {
                            case 'gateway_ids':
                              return (
                                <GatewaySelect
                                  defaultValue={initialGateways?.data}
                                  onChange={(options) =>
                                    options &&
                                    onChange(
                                      options.map((gateway) => gateway.id)
                                    )
                                  }
                                  isMulti
                                />
                              );
                            case 'deployment_ids':
                              return (
                                <DeploymentSelect
                                  defaultValue={initialDeployments?.data}
                                  onChange={(options) =>
                                    options &&
                                    onChange(
                                      options.map((deployment) => deployment.id)
                                    )
                                  }
                                  isMulti
                                />
                              );
                            case 'stream_ids':
                              return (
                                <StreamSelect
                                  defaultValue={initialStreams?.data}
                                  queryFilters={{
                                    stream_types: ['webrtc', 'rtsp'],
                                  }}
                                  onChange={(options) =>
                                    options &&
                                    onChange(
                                      options.map((deployment) => deployment.id)
                                    )
                                  }
                                  isMulti
                                />
                              );
                            case 'sources':
                              return (
                                <Select
                                  defaultValue={sources.map((source) =>
                                    STREAM_SOURCE_OPTIONS.find(
                                      ({ value }) => value === source
                                    )
                                  )}
                                  onChange={(options) =>
                                    options &&
                                    onChange(
                                      options.map((option) => option?.value)
                                    )
                                  }
                                  options={STREAM_SOURCE_OPTIONS}
                                  isMulti
                                />
                              );
                            default:
                              return <Select options={[]} isDisabled />;
                          }
                        }}
                        rules={{ required: true }}
                      />
                    ) : (
                      <Select options={[]} isDisabled />
                    )}
                  </div>
                  <IconButton
                    icon="cancel"
                    label="Remove filter"
                    onClick={() => {
                      setFilters((previousFilters) => {
                        const filterToRemove = previousFilters[index];
                        if (filterToRemove.entityType !== 'undecided') {
                          setValue(filterToRemove.entityType, []);
                        }
                        return previousFilters.filter((_, i) => i !== index);
                      });
                    }}
                  />
                </div>
              ))}
            </div>
            <div>
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  setFilters((previousFilters) => [
                    ...previousFilters,
                    { entityType: 'undecided' },
                  ]);
                }}
                disabled={
                  !canAdd ||
                  isLoading ||
                  filters.at(-1)?.entityType === 'undecided'
                }
              >
                <Icon name="plus" size="xsmall" />
                <span>Add filter</span>
              </Button>
            </div>
          </>
        )}
      </Field>

      <WidgetFormFooter isDirty={isDirty} isLoading={isUpdating || isLoading} />
    </form>
  );
}
